import { io } from 'socket.io-client';
import { useState, useEffect, useRef } from 'react';

let socket;
/**
 * @description initializeSocket is a function that initializes the socket connection
 * @param {string} token - JWT access token to send as query param
 * @returns {object} { socket }
 */
export const initializeSocket = (token) => {
	if (!socket) {
		socket = io(process.env.REACT_APP_WS_URL || process.env.REACT_APP_API_URL, {
			transports: ['websocket'],
			reconnection: true,
			reconnectionAttempts: 5,
			reconnectionDelay: 1000,
			query: token ? { token } : undefined,
		});
	}
	return socket;
};

/**
 * @description useSocket is a hook that initializes the socket connection
 * @param {string} topic - The topic to subscribe to
 * @param {string} token - JWT access token to send as query param
 * @returns {object} { socketConnection, isConnected, error }
 */
export const useSocket = (topic, token) => {
	const [isConnected, setIsConnected] = useState(false);
	const [error, setError] = useState(null);
	const socketRef = useRef(null);
	useEffect(() => {
		socketRef.current = initializeSocket(token);
		socketRef.current.on('connect', () => {
			setIsConnected(true);
			setError(null);

			if (topic) {
				socketRef.current.emit(topic);
			}
		});

		socketRef.current.on('connect_error', (err) => {
			setError(err.message);
			setIsConnected(false);
			console.debug('connect_error', err);
		});

		socketRef.current.on('disconnect', (reason) => {
			setIsConnected(false);
			console.debug('disconnect', reason);
		});

		socketRef.current.on('message', (data) => {
			console.debug('message', data);
		});
		// Ensure connection
		if (!socketRef.current.connected) {
			console.debug('connecting to socket');
			socketRef.current.connect();
		}

		return () => {
			if (topic) {
				socketRef.current.emit('unsubscribe', topic);
			}
		};
	}, [topic, token]);

	return { socketConnection: socketRef.current, isConnected, error };
};

/**
 * @description useSocketSubscription is a hook that subscribes to a socket event
 * @param {string} topic - The topic to subscribe to
 * @param {string} event - The event to listen for
 * @param {function} callback - The callback function to call when the event is received
 * @param {string} token - JWT access token to send as query param
 * @returns {object} { isConnected, error }
 */
export const useSocketSubscription = (topic, event, callback, token) => {
	const data = useSocket(topic, token);
	const { socketConnection, isConnected, error } = data;
	useEffect(() => {
		if (!socketConnection || !isConnected) return;

		// Listen for the specified event
		socketConnection.on(event, callback);

		// eslint-disable-next-line consistent-return
		return () => {
			socketConnection.off(event, callback);
		};
	}, [socketConnection, isConnected, event, callback]);

	return { isConnected, error };
};

/**
 * @description disconnectSocket is a function that disconnects the socket
 */
export const disconnectSocket = () => {
	if (socket) {
		socket.disconnect();
		socket = null;
	}
};
